import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { sleep } from '@sama/js-core';
import { lastValueFrom } from 'rxjs';
import { filter, map, retry } from 'rxjs/operators';
// eslint-disable-next-line import-whitelist -- ok in this case
import { RequestOptions as ResourceRequestOptions } from '../../../core/http/abstract-resource';

export type RetryableRequestResult = {
  response: ArrayBuffer;
  retryCount: number;
};

/**
 * This should be used for all expected errors during asset loading, so that
 * the given message will automatically show up in all the right places.
 */
export class AssetLoadError extends Error {
  constructor(
    message: string,
    public url?: string,
  ) {
    super(message);
  }
}

export class FrameLoadError extends Error {
  constructor(
    message: string,
    public retryCount: number,
  ) {
    super(message);
  }
}

// See url-request.service.spec-helpers.ts for the total amount of time needed
// for all retries in case you need to expect it in tests.
export const maxAssetLoadingRetries = 6;
export const skipDefaultErrorMessagingToken = new HttpContextToken(() => false);
export const retryDelay = 5000;

type RequestOptions = Pick<
  ResourceRequestOptions,
  | 'body'
  | 'responseType'
  | 'headers'
  | 'params'
  | 'retryCount'
  | 'skipDefaultErrorMessaging'
>;

/**
 * Inject this class directly when you need to send a request to a URL obtained
 * dynamically (such as a draft save). For requests to the hub server, use a
 * subclass with a name corresponding to the rails controller, e.g.
 * `TaskResource` for actions on the `tasks_controller`.
 *
 * For subclasses: each method should have the same name as the rails action,
 * e.g. `show()` or `submitAnswer()` for the task resource.
 */
@Injectable({ providedIn: 'root' })
export class UrlRequestService {
  private httpClient: HttpClient;

  constructor(injector: Injector) {
    this.httpClient = injector.get<HttpClient>(HttpClient);
  }

  /** @deprecated Use http-request.service.ts instead */
  request<T>(
    method: 'GET' | 'DELETE',
    url: string,
    options?: RequestOptions,
  ): Promise<T>;

  request<T>(
    method: 'PUT' | 'POST' | 'PATCH',
    url: string,
    options: RequestOptions & { body: any },
  ): Promise<T>;

  // eslint-disable-next-line max-lines-per-function -- https://github.com/eslint-stylistic/eslint-stylistic/issues/240, deprecated code that will be removed soon
  request<T>(
    method: string,
    url: string,
    {
      body,
      responseType,
      headers,
      params,
      retryCount = 0,
      skipDefaultErrorMessaging = false,
    }: RequestOptions & { body?: any } = { responseType: 'json' },
  ) {
    const req = new HttpRequest(method, url, body, {
      headers: new HttpHeaders({ Accept: 'application/json', ...headers }),
      params: params && new HttpParams({ fromString: $.param(params) }),
      responseType,
      context: new HttpContext().set(
        skipDefaultErrorMessagingToken,
        skipDefaultErrorMessaging,
      ),
    });
    return lastValueFrom(
      this.httpClient.request(req).pipe(
        retry({ delay: retryDelay, count: retryCount }),
        filter(
          (resp): resp is HttpResponse<unknown> =>
            resp.type === HttpEventType.Response,
        ),
        map((resp) => resp.body),
      ),
    ) as Promise<T>;
  }

  /** @deprecated Use http-request.service.ts instead */
  async downloadResourceWithRetries(
    url: string,
    options?: RequestOptions,
  ): Promise<RetryableRequestResult> {
    let retryCount = 0;
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    while (true) {
      try {
        const response = await this.downloadResource(url, options);
        return { response, retryCount };
      } catch (error) {
        if (retryCount >= maxAssetLoadingRetries) {
          throw new FrameLoadError((error as Error).message, retryCount);
        }

        await sleep(2 ** retryCount * 1000);
        ++retryCount;
      }
    }
  }

  /** @deprecated Use http-request.service.ts instead */
  async downloadResource(url: string, options?: RequestOptions) {
    try {
      return await this.request<ArrayBuffer>('GET', url, {
        responseType: 'arraybuffer',
        skipDefaultErrorMessaging: true,
        ...options,
      });
    } catch (error) {
      throw new AssetLoadError(
        determineAssetLoadErrorMessage(error as Error),
        url,
      );
    }
  }
}

export function determineAssetLoadErrorMessage(error: Error) {
  const status = error instanceof HttpErrorResponse && error.status;
  switch (status) {
    case 403:
      return 'Access denied';
    case 404:
      return `Asset does not exist at the given url`;
    case 0:
      return `Asset could not be accessed`;
    default:
      return `Asset failed to load. Please reload.`;
  }
}
